import { useMemo } from 'react';
import styled from 'styled-components';
import {
  Radio,
  RadioGroup,
  formFieldRequiredLabel as requiredLabel,
} from '@elementary/common-ui/components';
import { Operator } from '@elementary/common-ui/hooks/useUrlFilters';
import { TableAssetType } from '@elementary/common-ui/services/gql/types';
import { FormControlLabel as FormControlLabelBase } from '@mui/material';
import { RuleFormFields, RuleType } from 'shared/types';
import { Controller, UseFormReturn } from 'react-hook-form';
import { fontSize, space } from '@elementary/common-ui/style/utils';
import { RuleAsset } from '../../types';
import RuleFilterSelector from './RuleFilterSelector';
import RuleFilterChips from './RuleFilterChips';

const defaultOperators: Operator[] = [
  'equals',
  'notEquals',
  'contains',
  'notContains',
];

const filterableTableTypes: TableAssetType[] = [
  'MODEL',
  'SOURCE',
  'TABLE',
  'VIEW',
];

const FormControlLabel = styled(FormControlLabelBase)`
  gap: ${space('sm')};

  .MuiFormControlLabel-label {
    font-size: ${fontSize('base')};
  }
`;

enum FilterTypes {
  TAGS = 'tag',
  OWNERS = 'owner',
  STATUSES = 'status',
  MODELS = 'model',
}

type Props = {
  methods: UseFormReturn<RuleFormFields>;
  assets?: RuleAsset[];
  metadataData?: { tags: Array<string>; owners: Array<string> };
  showRuleTypeToggle?: boolean;
  disabled?: boolean;
};

const IncidentFilterFormContent = ({
  methods,
  assets,
  metadataData,
  showRuleTypeToggle,
  disabled,
}: Props) => {
  const ruleTypeIncidents = methods.watch('ruleTypeIncidents');
  const isFilterMode =
    !disabled && (!showRuleTypeToggle || ruleTypeIncidents === 'filter');

  const filterOptions = useMemo(
    () =>
      [
        {
          property: FilterTypes.TAGS,
          propertyLabel: 'Tag',
          options: (metadataData?.tags || []).map((tag) => ({
            label: tag,
            value: tag,
          })),
          multiselect: true,
        },
        {
          property: FilterTypes.OWNERS,
          propertyLabel: 'Owner',
          options: (metadataData?.owners || []).map((owner) => ({
            label: owner,
            value: owner,
          })),
          multiselect: true,
        },
        {
          property: FilterTypes.STATUSES,
          propertyLabel: 'Status',
          options: [
            { value: 'warn', label: 'Warn' },
            { value: 'fail', label: 'Fail' },
            { value: 'error', label: 'Error' },
          ],
          multiselect: true,
          isSingularValue: true,
          showSearch: false,
        },
        {
          property: FilterTypes.MODELS,
          propertyLabel: 'Model',
          options: (assets || [])
            .filter((asset) => filterableTableTypes.includes(asset.tableType))
            .map((asset) =>
              asset.tableType === 'SOURCE' && asset.sourceName
                ? {
                    value: `${asset.sourceName}.${asset.name}`,
                    label: `${asset.sourceName}.${asset.name}`,
                  }
                : { value: asset.name, label: asset.name },
            ),
          multiselect: true,
        },
      ].filter((o) => o.options.length > 0),
    [assets, metadataData],
  );

  return (
    <>
      {showRuleTypeToggle && (
        <Controller
          control={methods.control}
          name="ruleTypeIncidents"
          rules={{ required: requiredLabel }}
          render={({ field }) => (
            <RadioGroup
              {...field}
              disabled={disabled}
              sx={{ gap: 2 }}
              onChange={(_, value) => {
                if (value != null) {
                  methods.setValue('ruleTypeIncidents', value as RuleType, {
                    shouldValidate: true,
                  });
                  if (value === 'all') {
                    methods.reset({
                      ...methods.getValues(),
                      incidentFilters: [],
                    });
                  }
                }
              }}
            >
              <FormControlLabel
                sx={{ m: 0 }}
                value="all"
                control={<Radio sx={{ paddingY: 0, pl: '9px', pr: '2px' }} />}
                label="All alerts"
              />
              <FormControlLabel
                sx={{ m: 0 }}
                value="filter"
                control={<Radio sx={{ paddingY: 0, pl: '9px', pr: '2px' }} />}
                label="Filter alerts"
              />
            </RadioGroup>
          )}
        />
      )}
      <Controller
        name="incidentFilters"
        control={methods.control}
        rules={{
          required: showRuleTypeToggle && isFilterMode ? requiredLabel : false,
        }}
        render={({ field }) => (
          <RuleFilterSelector
            field={field}
            methods={methods}
            type="incidentFilters"
            disabled={!isFilterMode}
            filterOptions={filterOptions}
            defaultOperators={defaultOperators}
          />
        )}
      />

      <RuleFilterChips type="incidentFilters" filterOptions={filterOptions} />
    </>
  );
};

export default IncidentFilterFormContent;
